import type {OperationVariables} from "@apollo/client"
import {ApolloError, type ApolloQueryResult} from "@apollo/client/core"
import type {QueryOptions as CoreQueryOptions} from "@apollo/client/core/watchQueryOptions"
import {firstValueFrom, type Observable} from "rxjs"

export type EmptyObject = {
    [key: string]: unknown
}

export interface QueryOptionsAlone<TVariables = EmptyObject, TData = unknown> extends Omit<CoreQueryOptions<TVariables, TData>, "query" | "variables"> {}

export const fetchIgnoringErrors =
    <ResultType = object, VariablesType extends OperationVariables = EmptyObject>(query: {
        fetch: (variables?: VariablesType, options?: QueryOptionsAlone<VariablesType, ResultType>) => Observable<ApolloQueryResult<ResultType>>
    }) =>
    (variables?: VariablesType, options?: QueryOptionsAlone<VariablesType, ResultType>) =>
        firstValueFrom(query.fetch(variables, {...options, errorPolicy: "all"})).then(({data}) => data)

export const fetchThrowingErrors =
    <ResultType = object, VariablesType extends OperationVariables = EmptyObject>(query: {
        fetch: (variables?: VariablesType, options?: QueryOptionsAlone<VariablesType, ResultType>) => Observable<ApolloQueryResult<ResultType>>
    }): ((variables: VariablesType, options?: QueryOptionsAlone<VariablesType, ResultType>) => Promise<ResultType>) =>
    (variables?: VariablesType, options?: QueryOptionsAlone<VariablesType, ResultType>) =>
        firstValueFrom(query.fetch(variables, {...options, errorPolicy: "all"})).then(({data, error, errors}) => {
            // With errorPolicy "all", Apollo populates `errors` (the raw GraphQL error array)
            // but NOT `error` (the ApolloError wrapper). We need to check both.
            const graphQlErrors = error?.graphQLErrors ?? errors
            if (graphQlErrors && graphQlErrors.length > 0) {
                const onlyExpectedErrors = graphQlErrors.every((e) => e.extensions?.["expected"])
                if (!onlyExpectedErrors) {
                    throw error ?? new ApolloError({graphQLErrors: [...graphQlErrors]})
                }
            }
            if (error?.networkError) {
                throw error
            }
            return data
        })
